import { Reducer } from 'redux';
import { codetableOptionsReducer } from '../common';
import { mapToDeletedState, mapToRequestDeletingStatus, mapToFailedDeletingStatus } from './mapping/map-delete';
import { mapToDecreasedChildrenCount } from './mapping/map-to-decreased-children-count';
import {
  ACTION, RequestCodeItems, ReceiveCodeItems, FailCodeItems,
  RequestCodeItemDelete, ReceiveCodeItemDelete, FailCodeItemDelete,
  ExpandCodeItem, InvalidateCodeItems,
  UpdateCategory, RequestCodetableAdministrationList,
  ReceiveCodetableAdministrationList, FailCodetableAdministrationList, UpdateIncludeInactive,
  ClearCodesAccess, RequestCodesAccess, ReceiveCodesAccess, FailCodesAccess, UpdateSearchModel,
   ResetListState
} from './action-types';
import { defaultIncludeInactiveCodes } from './constants';
import { getDirectParentsList, setLoadingState } from './reducers-helpers';
import { compareStateWithRequestedProps } from './selectors';
import { CodetableListState } from './types-store';

export const INITIAL_CODETABLE_LIST_STATE: CodetableListState = {
  itemAccess: undefined,
  category: undefined,
  fetchingCodetableId: undefined,
  fetchingBreadcrumbsCodetableId: undefined,
  fetchingItemAccess: undefined,
  fetchingCodetableItemAccess: undefined,
  includeInactive: defaultIncludeInactiveCodes,
  codeListBranchState: [],
  codeListBranch: [],
  codetableOptions: {},
  options: [],
  requestedSearchModel: undefined
};

const reducers: { [indexer: string]: Reducer<CodetableListState> } = {

  ...codetableOptionsReducer as { [indexer: string]: Reducer<CodetableListState> },

  [ACTION.REQUEST_CODE_ITEMS]: (state: CodetableListState, action: RequestCodeItems): CodetableListState => {

    const { codetableId, parentId, categoryId, searchModel } = action;

    // if any of request parameter has been changed - reload whole state
    if (!compareStateWithRequestedProps(state, codetableId, categoryId, searchModel)) {
      return {
        ...state,
        fetchingCodetableId: codetableId,
        codeListBranchState: [
          {
            id: parentId,
            fetching: true,
            open: parentId === undefined
          }
        ],
        codeListBranch: []
      };
    }

    const currentBranchState = state.codeListBranchState.find(s => s.id === parentId) || { open: false };
    return {
      ...state,
      codeListBranchState: [
        ...state.codeListBranchState.filter(s => s !== currentBranchState),
        {
          ...currentBranchState,
          id: parentId,
          fetching: true,
          open: parentId === undefined || currentBranchState.open
        }
      ]
    };
  },

  [ACTION.RECEIVE_CODE_ITEMS]: (state: CodetableListState, action: ReceiveCodeItems): CodetableListState => {

    const { parentId, codeItems, numberOfResults, clearList } = action;

    const currentBranchState = state.codeListBranchState.find(s => s.id === parentId) ||
      { id: parentId, fetching: false };
    const currentBranch = state.codeListBranch.find(branch => branch.id === parentId);
    const currentCodeItems = currentBranch && currentBranch.items || [];

    return {
      ...state,
      codeListBranchState: [
        ...state.codeListBranchState.filter(s => s !== currentBranchState),
        {
          ...currentBranchState,
          fetching: false,
          needsReload: false
        }
      ],
      codeListBranch: [
        ...state.codeListBranch.filter(branch => branch.id !== parentId),
        {
          id: parentId,
          numberOfResults,
          items: clearList
            ? codeItems
            : [
              ...currentCodeItems,
              ...codeItems
            ]
        }
      ]
    };
  },

  [ACTION.FAIL_CODE_ITEMS]: (state: CodetableListState, action: FailCodeItems): CodetableListState => {

    const { codetableId, parentId, categoryId, searchModel: query } = action;

    const currentBranchState = state.codeListBranchState.find(s => s.id === parentId) ||
      { id: parentId, fetching: false };
    const currentBranch = state.codeListBranch.find(branch => branch.id === parentId) ||
      { id: parentId, numberOfResults: 0, items: [] };

    return currentBranchState.fetching && compareStateWithRequestedProps(state, codetableId, categoryId, query)
      ? {
        ...state,
        codeListBranchState: [
          ...state.codeListBranchState.filter(s => s !== currentBranchState),
          {
            ...currentBranchState,
            fetching: false,
            needsReload: false
          }
        ],
        codeListBranch: [
          ...state.codeListBranch.filter(branch => branch !== currentBranch),
          currentBranch
        ]
      }
      : state;
  },

  [ACTION.REQUEST_CODETABLE_ADMINISTRATION_LIST]:
    (state: CodetableListState, action: RequestCodetableAdministrationList): CodetableListState => {

      return {
        ...state,
        fetchingBreadcrumbsCodetableId: true
      };
    },

  [ACTION.RECEIVE_CODETABLE_ADMINISTRATION_LIST]:
    (state: CodetableListState, action: ReceiveCodetableAdministrationList): CodetableListState => {

      const { options } = action;

      return {
        ...state,
        fetchingBreadcrumbsCodetableId: false,
        options
      };
    },

  [ACTION.FAIL_CODETABLE_ADMINISTRATION_LIST]:
    (state: CodetableListState, action: FailCodetableAdministrationList): CodetableListState => {

      return {
        ...state,
        fetchingBreadcrumbsCodetableId: false,
        options: []
      };
    },

  [ACTION.REQUEST_CODE_ITEM_DELETE]: (state: CodetableListState, action: RequestCodeItemDelete): CodetableListState => {

    const parents = getDirectParentsList(state.codeListBranch, action.codeId);

    const codeListBranchState = setLoadingState(state.codeListBranchState, parents, true);

    const codeListBranch = mapToRequestDeletingStatus(state, action);

    return {
      ...state,
      codeListBranch,
      codeListBranchState
    };
  },

  [ACTION.RECEIVE_CODE_ITEM_DELETE]: (state: CodetableListState, action: ReceiveCodeItemDelete): CodetableListState => {

    const codeListBranchDecreasedChildren = mapToDecreasedChildrenCount(state, action);
    const { codeListBranch: codeListBranchUpdated, codeListBranchState: codeListBranchStateUpdated } =
      mapToDeletedState({ ...state, codeListBranch: codeListBranchDecreasedChildren }, action);

    const parents = getDirectParentsList(codeListBranchDecreasedChildren, action.codeId);

    const codeListBranchState = setLoadingState(codeListBranchStateUpdated, parents, false);

    return {
      ...state,
      codeListBranch: codeListBranchUpdated,
      codeListBranchState
    };
  },

  [ACTION.FAIL_CODE_ITEM_DELETE]: (state: CodetableListState, action: FailCodeItemDelete): CodetableListState => {

    const codeListBranch = mapToFailedDeletingStatus(state, action);

    const parents = getDirectParentsList(codeListBranch, action.codeId);

    const codeListBranchState = setLoadingState(state.codeListBranchState, parents, false);

    return {
      ...state,
      codeListBranch,
      codeListBranchState
    };
  },

  [ACTION.EXPAND_CODE_ITEM]: (state: CodetableListState, action: ExpandCodeItem): CodetableListState => {

    const currentBranchState = state.codeListBranchState.find(s => s.id === action.parentId) ||
      { id: action.parentId, open: false };

    return {
      ...state,
      codeListBranchState: [
        ...state.codeListBranchState.filter(s => s !== currentBranchState),
        {
          ...currentBranchState,
          open: !currentBranchState.open
        }
      ]
    };
  },

  [ACTION.INVALIDATE_CODE_ITEMS]: (state: CodetableListState, action: InvalidateCodeItems): CodetableListState => {

    return {
      ...state,
      codeListBranchState: state.codeListBranchState.map(codeState => ({
        ...codeState,
        fetching: false,
        needsReload: true
      }))
    };
  },

  [ACTION.UPDATE_CATEGORY]: (state: CodetableListState, action: UpdateCategory): CodetableListState => {

    return {
      ...state,
      category: action.value
    };
  },

  [ACTION.UPDATE_INCLUDE_INACTIVE]: (state: CodetableListState, action: UpdateIncludeInactive): CodetableListState => {

    return {
      ...state,
      includeInactive: action.value
    };
  },

  [ACTION.CLEAR_CODES_ACCESS]: (state: CodetableListState, action: ClearCodesAccess): CodetableListState => {

    return {
      ...state,
      itemAccess: undefined
    };
  },

  [ACTION.REQUEST_CODES_ACCESS]: (state: CodetableListState, action: RequestCodesAccess): CodetableListState => {

    return {
      ...state,
      fetchingItemAccess: action.codeId
    };
  },

  [ACTION.RECEIVE_CODES_ACCESS]: (state: CodetableListState, action: ReceiveCodesAccess): CodetableListState => {
    return action.accessCodes && action.accessCodes.id === state.fetchingItemAccess
      ? {
        ...state,
        fetchingItemAccess: undefined,
        itemAccess: action.accessCodes
      }
      : state;
  },

  [ACTION.FAIL_CODES_ACCESS]: (state: CodetableListState, action: FailCodesAccess): CodetableListState => {
    return {
      ...state,
      fetchingItemAccess: undefined
    };
  },

  [ACTION.UPDATE_SEARCH_MODEL]: (state: CodetableListState, action: UpdateSearchModel): CodetableListState => {
    return {
      ...state,
      requestedSearchModel: action.model
    };
  },

  [ACTION.RESET_LIST_STATE]: (state: CodetableListState, action: ResetListState): CodetableListState => {
    return {
      ...INITIAL_CODETABLE_LIST_STATE,
      category: state.category,
      fetchingCodetableId: state.fetchingCodetableId
    };
  }
};

export const listReducer: Reducer<CodetableListState> = (
  state: CodetableListState = INITIAL_CODETABLE_LIST_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
